import { DRAFT_SECTION_SENTINEL } from '@/components/tiptap/shared/draft-sentinel';
import type { NodeViewProps } from '@tiptap/react';
import { useEffect, useRef } from 'react';

const normalizeSectionText = (text: string | null | undefined) =>
  text?.replace(DRAFT_SECTION_SENTINEL, '').trim().toLowerCase() ?? '';

export const isDraftSectionEmpty = (node: NodeViewProps['node']): boolean => {
  const contentNodes = node.content.content;

  if (contentNodes.length !== 2) {
    return false;
  }

  const [headingNode, bodyNode] = contentNodes;

  if (!headingNode || !bodyNode) {
    return false;
  }

  if (headingNode.type.name !== 'heading' || bodyNode.type.name !== 'paragraph') {
    return false;
  }

  const headingText = normalizeSectionText(headingNode.textContent);
  const bodyText = normalizeSectionText(bodyNode.textContent);

  const hasEmptyTitle = headingText === '';
  const hasEmptyBody = bodyText === '';

  return hasEmptyTitle && hasEmptyBody;
};

interface UseBlurEmptySectionParams {
  isDraftSection: boolean;
  isFocused: boolean;
  isLinkEhrOpen: boolean;
  isComputerUseNotePushMappingOpen: boolean;
  isSectionActionsOpen: boolean;
  node: NodeViewProps['node'];
  onDelete: () => void;
}

/**
 * Deletes a draft section when the user blurs away from it without adding
 * meaningful content. The dropdown-open guards prevent false-positive deletion
 * when toolbar interactions temporarily steal focus from the editor.
 */
export const useBlurEmptySection = ({
  isDraftSection,
  isFocused,
  isLinkEhrOpen,
  isComputerUseNotePushMappingOpen,
  isSectionActionsOpen,
  node,
  onDelete,
}: UseBlurEmptySectionParams): void => {
  const wasFocusedRef = useRef(false);

  useEffect(() => {
    const wasFocused = wasFocusedRef.current;
    wasFocusedRef.current = isFocused;

    if (
      !isDraftSection ||
      !wasFocused ||
      isFocused ||
      isLinkEhrOpen ||
      isComputerUseNotePushMappingOpen ||
      isSectionActionsOpen ||
      !isDraftSectionEmpty(node)
    ) {
      return;
    }

    onDelete();
  }, [
    isDraftSection,
    isFocused,
    isLinkEhrOpen,
    isComputerUseNotePushMappingOpen,
    isSectionActionsOpen,
    node,
    onDelete,
  ]);
};
